import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { preferencesService, UserPreferences } from '../services/preferencesService';
import { useAuth } from '../contexts/AuthContext';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [notificationTime, setNotificationTime] = useState('08:00');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const data = await preferencesService.getPreferences();
      setPreferences(data);
      setNotificationTime(data.notificationTime);
      setNotificationsEnabled(data.notificationsEnabled);
      setError('');
    } catch (err: any) {
      setError('Erro ao carregar preferências');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await preferencesService.updatePreferences({
        notificationTime,
        notificationsEnabled,
      });
      setSuccess('Preferências salvas com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      fetchPreferences();
    } catch (err: any) {
      setError('Erro ao salvar preferências');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#4CAF50' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20px 20px, rgba(76, 175, 80, 0.05) 2px, transparent 0)`,
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
      {/* Header com informações do usuário */}
      <Box
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
          boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 20px 20px, rgba(255, 255, 255, 0.1) 2px, transparent 0)`,
            backgroundSize: '40px 40px',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.25)',
              backdropFilter: 'blur(10px)',
              border: '3px solid rgba(255,255,255,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: 700,
              color: 'white',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || <PersonIcon sx={{ fontSize: 40 }} />}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 0.5,
                textShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              {user?.name || 'Usuário'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              📧 {user?.email || 'email@exemplo.com'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Info box sobre como funciona */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 187, 106, 0.05) 100%)',
          border: '2px solid rgba(76, 175, 80, 0.2)',
          boxShadow: '0 2px 12px rgba(76, 175, 80, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              fontSize: '2rem',
              lineHeight: 1,
            }}
          >
            💡
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D3748', mb: 1 }}>
              Como funciona o sistema de devocionais
            </Typography>
            <Typography variant="body2" sx={{ color: '#4A5568', lineHeight: 1.8, mb: 1 }}>
              • O <strong>Dia 1</strong> está sempre disponível para começar sua jornada
            </Typography>
            <Typography variant="body2" sx={{ color: '#4A5568', lineHeight: 1.8, mb: 1 }}>
              • Cada novo devocional é <strong>liberado à meia-noite</strong> após visualizar o anterior
            </Typography>
            <Typography variant="body2" sx={{ color: '#4A5568', lineHeight: 1.8 }}>
              • Receba uma <strong>notificação</strong> no horário escolhido quando um novo devocional estiver disponível
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              }}
            >
              <NotificationsIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D3748', mb: 0.5 }}>
                Preferências de Notificação
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Personalize quando e como você deseja ser notificado
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.08)' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Switch de notificações */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                background: notificationsEnabled
                  ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(102, 187, 106, 0.02) 100%)'
                  : 'rgba(0,0,0,0.02)',
                border: '2px solid',
                borderColor: notificationsEnabled ? 'rgba(76, 175, 80, 0.2)' : 'rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4CAF50',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4CAF50',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2D3748', mb: 0.5 }}>
                      Habilitar Notificações
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096', lineHeight: 1.6 }}>
                      Receba lembretes diários quando um novo devocional for liberado
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>

            {/* Horário de notificação */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 187, 106, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ScheduleIcon sx={{ color: '#4CAF50', fontSize: 22 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D3748' }}>
                  Horário de Notificação
                </Typography>
              </Box>
              <TextField
                fullWidth
                type="time"
                value={notificationTime}
                onChange={(e) => setNotificationTime(e.target.value)}
                disabled={!notificationsEnabled}
                InputLabelProps={{ shrink: true }}
                helperText="Escolha o horário que deseja ser notificado sobre novos devocionais"
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 14px',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4CAF50',
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(0,0,0,0.08)' }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                color: 'white',
                px: 5,
                py: 1.75,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.35)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
                  boxShadow: '0 8px 24px rgba(76, 175, 80, 0.45)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(0,0,0,0.12)',
                  color: 'rgba(0,0,0,0.26)',
                },
              }}
            >
              {saving ? 'Salvando...' : 'Salvar Preferências'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default Profile;
